/**
 * AdminEditorProfile — every admin has one editor profile that renders
 * as a hover card on the public guides they own. Big trust signal for
 * readers.
 *
 * Fields: avatar, display name, blurb, up-to-5 social links.
 * Photo upload reuses the existing venue upload endpoint.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Save, Loader2, Upload, User as UserIcon,
  Instagram, Facebook, Youtube, Globe, Music2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCIALS = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
  { key: 'facebook', label: 'Facebook', Icon: Facebook, placeholder: 'https://facebook.com/yourpage' },
  { key: 'tiktok', label: 'TikTok', Icon: Music2, placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'youtube', label: 'YouTube', Icon: Youtube, placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'website', label: 'Website', Icon: Globe, placeholder: 'https://your-site.com' },
];

export default function AdminEditorProfile() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [profile, setProfile] = useState({
    display_name: '',
    avatar_url: '',
    blurb: '',
    social_links: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/editor-profiles/me`, { headers });
        setProfile({
          display_name: r.data?.display_name || '',
          avatar_url: r.data?.avatar_url || '',
          blurb: r.data?.blurb || '',
          social_links: r.data?.social_links || {},
        });
      } catch {
        toast.error('Couldn’t load your editor profile');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) return toast.error('Pick an image file');
    setUploading(true);
    const t = toast.loading('Uploading avatar…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/venues/upload-photo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.url) {
        setProfile((p) => ({ ...p, avatar_url: r.data.url }));
        toast.success('Avatar ready — hit Save to apply', { id: t });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed', { id: t });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/editor-profiles/me`, {
        display_name: profile.display_name?.trim() || '',
        avatar_url: profile.avatar_url || null,
        blurb: profile.blurb || '',
        social_links: profile.social_links || {},
      }, { headers });
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setSocial = (key, val) =>
    setProfile((p) => ({ ...p, social_links: { ...(p.social_links || {}), [key]: val } }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-fuchsia-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="admin-editor-profile-page">
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <Link
          to="/admin/guides"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="editor-profile-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to guides
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-['Space_Grotesk']">
            Editor profile
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Shown as a hover card next to your byline on every guide you publish. This is the biggest trust signal a reader gets before they trust your picks.
          </p>
        </header>

        <div className="space-y-6 bg-card border border-border rounded-2xl p-5 sm:p-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="h-20 w-20 rounded-full border-2 border-dashed border-border overflow-hidden bg-gradient-to-br from-fuchsia-500/20 to-indigo-500/20 flex items-center justify-center cursor-pointer hover:border-fuchsia-500/50 shrink-0"
              data-testid="editor-avatar-slot"
            >
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
                data-testid="editor-avatar-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="editor-avatar-upload-btn"
              >
                {uploading
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload avatar</>}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-1">JPG, PNG, WebP · square works best (600x600 or larger).</p>
            </div>
          </div>

          {/* Display name */}
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <Input
              value={profile.display_name || ''}
              onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))}
              placeholder="e.g. Alex Ramirez"
              data-testid="editor-display-name"
            />
          </div>

          {/* Blurb */}
          <div className="space-y-1.5">
            <Label>Blurb <span className="text-muted-foreground font-normal">— shows on the hover card (2–3 sentences)</span></Label>
            <Textarea
              rows={3}
              value={profile.blurb || ''}
              onChange={(e) => setProfile((p) => ({ ...p, blurb: e.target.value }))}
              placeholder="Denver-based food editor covering wine bars, breweries, and hidden-gem restaurants. Ex-line cook, current wine dork."
              maxLength={400}
              data-testid="editor-blurb"
            />
            <p className="text-[11px] text-muted-foreground text-right">
              {(profile.blurb || '').length}/400
            </p>
          </div>

          {/* Social links */}
          <div className="space-y-2">
            <Label>Social links <span className="text-muted-foreground font-normal">— leave any blank you don’t use</span></Label>
            {SOCIALS.map(({ key, label, Icon, placeholder }) => (
              <div key={key} className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-fuchsia-300 shrink-0" />
                <span className="text-xs font-semibold w-20 text-muted-foreground shrink-0">{label}</span>
                <Input
                  value={(profile.social_links || {})[key] || ''}
                  onChange={(e) => setSocial(key, e.target.value)}
                  placeholder={placeholder}
                  className="flex-1"
                  data-testid={`editor-social-${key}`}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold"
              data-testid="editor-profile-save"
            >
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save profile</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
